'use client';

/*
 *@providesModule AWSManager
 *
 */

import {AWSConstants, CurrentUserStore, SKZUser} from '@sdk-mobile';
import {datadogRum} from '@datadog/browser-rum';
import Cookies from 'js-cookie';
import {
  getCurrentGameCookieName,
  INSTALL_ID_COOKIE_NAME,
  SKILLZ_APP_COOKIE_NAME,
} from '@/utils/constant';
import {getEnvConfig} from '@/utils/getEnvConfig';
import {fingerprintStore} from '@/utils/fingerprints/FingerprintStore';
import {getEnvironmentFromCookie} from '@/utils/getEnvironmentFromCookie';
import getGameId from '@/utils/getGameId';
type ValidEvents = keyof typeof AWSConstants;

// the param keys maps to `A_<key>` column in `awsma.v_event` table.
// many of the values are auto populated in SKZCognitoManager.setupGlobalAttributes
type ParamKeys = 'category' | 'type' | 'data' | 'result' | 'time' | 'stars' | 'id';

interface DefaultEventParams {
  otaVersion: string;
  sdkVersion: string;
  gameVersion?: string;
  username?: string;
  userId?: string;
  environment?: string;
  gameId?: string;
  otaURL: string;
  bundleId: string;
  deviceId?: string;
  model: string;
  platform: string;
  OS: string;
  skillzApp?: string;
}

// the param keys maps to `M_<key>` column in `awsma.v_event` table.
type MetricKeys = 'amount' | 'bonus';

type Params = Partial<Record<ParamKeys, string | number>>;
type Metrics = Partial<Record<MetricKeys, number>>;

const AWSManager = {
  trackEvent: (name: ValidEvents, params: Params = {}, metrics: Metrics = {}) => {
    // Merge params and metrics

    AWSManager.getDefaultEventParams().then((defaultParams) => {
      const eventParams: {[key: string]: string | number} = {
        ...params,
        ...metrics,
        ...defaultParams,
      };

      try {
        //@ts-ignore
        if (typeof window !== 'undefined' && window.dataLayer) {
          //@ts-ignore
          window.dataLayer.push({
            event: name,
            ...eventParams,
          });
          //@ts-ignore
          datadogRum.addAction(name, eventParams);
        } else {
          console.warn('gtag function is not available');
        }
      } catch (error) {
        console.error('Error tracking event with gtag:', error);
      }
    });
  },

  getDefaultEventParams: async (): Promise<DefaultEventParams> => {
    let userData: SKZUser | undefined;
    try {
      userData = await CurrentUserStore.getUserData(true);
    } catch (error) {
      console.error('Error getting user data from store for analytics:', error);
    }

    const otaVersion = '2025.1.309'; // Function to retrieve OTA version
    const sdkVersion = '2025.0.76'; // Replace with actual SDK version
    const otaURL = 'web'; // No OTA URL for Web
    const gameIdString = Cookies.get(getCurrentGameCookieName(getGameId())); // Replace with actual game ID retrieval logic
    let gameId;
    try {
      if (gameIdString) {
        const decodedString = atob(gameIdString);
        const jsonObject = JSON.parse(decodedString);
        gameId = jsonObject.gid;
      }
    } catch (error) {
      console.log('Error parsing game id for aws event: ', error);
    }

    const skillzApp = Cookies.get(SKILLZ_APP_COOKIE_NAME) === '1' ? 'true' : 'false';
    const userId = Cookies.get(getEnvConfig().CURRENT_USER_COOKIE_NAME); // Replace with actual user ID retrieval logic
    const bundleId = 'web'; // Replace with the actual bundle identifier retrieval logic
    const deviceId = Cookies.get(INSTALL_ID_COOKIE_NAME); // Replace with actual device ID retrieval logic, such as from a custom UUID
    const gameVersion = fingerprintStore.getUnityConfigObject(gameId || '')?.productVersion || '';
    const model = navigator.userAgent; // Replace with actual logic for determining the device model
    const platform = 'web'; // Assuming platform is web for this version
    const OS = navigator.userAgent; // Retrieve OS version or user agent
    const username = userData?.username;
    const environment = getEnvironmentFromCookie();
    const defaultParams: DefaultEventParams = {
      otaVersion,
      sdkVersion,
      otaURL,
      bundleId,
      model,
      platform,
      OS,
      gameId,
      userId,
      deviceId,
      username,
      gameVersion,
      environment,
      skillzApp,
    };

    return defaultParams;
  },
};

export default AWSManager;
